/**
 * The text CenoX AI read out of one project document.
 *
 * Shown so somebody can check what the assistant will quote before it quotes
 * it, and so a citation in an answer can open the exact passage it came from.
 * Opened from the Files panel at the start, or from a `[n]` in an answer at
 * that passage's position (`ordinal - 1`), which is then marked.
 *
 * A drawer rather than the shared `Modal`: it opens from inside the history
 * sheet as well as from the conversation, and a `<dialog>` in the top layer
 * would sit outside that sheet's focus trap. Escape is handled on the drawer
 * itself and marked as handled, so it closes this and not the sheet under it.
 *
 * Every chunk is rendered as text. Nothing a document contains is ever
 * treated as markup.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { AiProjectFileText } from '@cenox/shared';
import { ApiError, api } from '../../../lib/api';
import { useBusinessPath } from '../../../lib/auth';
import { Spinner, cn } from '../../../components/ui';

/** The server's page size for `/text`. */
export const TEXT_PAGE = 50;

/** What opens the drawer: a file, and where in its text to start. */
export interface FileTextTarget {
  projectId: string;
  fileId: string;
  title: string | null;
  /** Zero-based position in the file's chunks; a citation's `ordinal - 1`. */
  offset: number;
  /** The ordinal to mark, when a citation opened it. */
  focusOrdinal?: number | null;
}

export function FileTextDrawer({ target, onClose }: { target: FileTextTarget; onClose: () => void }): ReactNode {
  const path = useBusinessPath();
  const [start, setStart] = useState(() => Math.max(0, target.offset));
  const focused = useRef<HTMLLIElement | null>(null);
  const closeButton = useRef<HTMLButtonElement | null>(null);

  // A different citation in the same drawer starts again where it points.
  useEffect(() => {
    setStart(Math.max(0, target.offset));
  }, [target.fileId, target.offset]);

  // Into the drawer on opening, and back to whatever opened it on closing.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    closeButton.current?.focus();
    return () => {
      if (opener && document.contains(opener)) opener.focus();
    };
  }, []);

  const text = useQuery({
    queryKey: ['ai-file-text', target.projectId, target.fileId, start],
    queryFn: () =>
      api.get<AiProjectFileText>(
        path(`/ai/projects/${target.projectId}/files/${target.fileId}/text?offset=${start}`),
      ),
  });

  useEffect(() => {
    focused.current?.scrollIntoView?.({ block: 'start' });
  }, [text.data]);

  const chunks = text.data?.chunks ?? [];
  const total = text.data?.totalChunks ?? 0;
  const hasPrevious = start > 0;
  const hasNext = start + chunks.length < total;

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close document text"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 bg-black/30 backdrop-blur-[1px]"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Text of ${target.title ?? 'document'}`}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
          }
        }}
        className="absolute inset-y-0 right-0 flex w-[min(36rem,94vw)] flex-col bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-2 border-b border-slate-200 px-4 py-3">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold text-slate-900">{target.title ?? 'Document'}</h2>
            <p className="text-xs text-slate-500">
              The text CenoX AI reads from this document
              {total > 0 ? ` · passages ${start + 1}–${start + chunks.length} of ${total}` : ''}
            </p>
          </div>
          <button
            ref={closeButton}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {text.isPending && <Spinner label="Loading text" />}
          {text.isError && (
            <p role="alert" className="text-sm text-red-700">
              {text.error instanceof ApiError ? text.error.message : 'Could not load the text of that document.'}
            </p>
          )}
          {text.data?.statusDetail && (
            <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">{text.data.statusDetail}</p>
          )}
          {text.data && chunks.length === 0 && (
            <p className="text-sm text-slate-500">No text was read from this part of the document.</p>
          )}
          <ol className="space-y-3">
            {chunks.map((c) => {
              const marked = target.focusOrdinal != null && c.ordinal === target.focusOrdinal;
              return (
                <li
                  key={c.ordinal}
                  ref={marked ? focused : undefined}
                  aria-current={marked ? 'true' : undefined}
                  className={cn(
                    'rounded-xl border px-3 py-2',
                    marked ? 'border-brand-300 bg-brand-50' : 'border-slate-200',
                  )}
                >
                  <p className="text-[11px] font-medium text-slate-500">
                    {c.locator ?? `Passage ${c.ordinal}`}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-800">{c.content}</p>
                </li>
              );
            })}
          </ol>
        </div>

        {(hasPrevious || hasNext) && (
          <div className="flex items-center justify-between border-t border-slate-200 px-4 py-2">
            <button
              type="button"
              disabled={!hasPrevious || text.isFetching}
              onClick={() => setStart(Math.max(0, start - TEXT_PAGE))}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-40"
            >
              <ChevronLeft className="size-3.5" aria-hidden />
              Earlier
            </button>
            <button
              type="button"
              disabled={!hasNext || text.isFetching}
              onClick={() => setStart(start + chunks.length)}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-40"
            >
              Later
              <ChevronRight className="size-3.5" aria-hidden />
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}
